import { useEffect, useState } from "react";
import { Ban, Loader2, UserCheck, PauseCircle, MailWarning } from "lucide-react";
import api from "@/lib/api";
import { UnsubContactsDialog } from "@/components/UnsubContactsDialog";

export function UnsubscribesReport({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  useEffect(() => {
    setLoading(true);
    api.get("/reports/unsubscribes", { params: { start: range.start, end: range.end } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range.start, range.end]);

  const worst = data?.by_campaign?.find((r) => r.rate > 0);
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="report-unsubscribes-wrap">
      <div className="px-5 py-4 border-b border-[#1F2937] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Ban className="h-5 w-5 text-[#EF4444]" />
          <h3 className="font-display text-lg font-semibold text-slate-200">Disiscrizioni (STOP) per campagna</h3>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap" data-testid="report-unsubscribes-totals">
            <span>STOP ricevuti: <b className="text-[#EF4444]" data-testid="report-unsubscribes-total">{data.total}</b></span>
            <span className="inline-flex items-center gap-1"><UserCheck className="h-3.5 w-3.5 text-[#10B981]" /> Re-iscritti: <b className="text-[#10B981]">{data.resubscribed}</b></span>
            {(data.paused > 0 || data.paused_now > 0) && <span className="inline-flex items-center gap-1"><PauseCircle className="h-3.5 w-3.5 text-[#F59E0B]" /> Hanno scelto la pausa: <b className="text-[#F59E0B]" data-testid="report-unsubscribes-paused">{data.paused}</b> · in pausa ora: <b className="text-[#F59E0B]">{data.paused_now}</b></span>}
            {data.unattributed > 0 && <span>Senza campagna recente: <b className="text-slate-300">{data.unattributed}</b></span>}
            {worst && <span>Più fastidiosa: <b className="text-[#F59E0B]" data-testid="report-unsubscribes-worst">{worst.name}</b> · {worst.rate}%</span>}
          </div>
        )}
      </div>
      {loading ? (
        <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
      ) : !data || data.total === 0 ? (
        <p className="p-5 text-sm text-slate-500" data-testid="report-unsubscribes-empty">Nessuno STOP ricevuto nel periodo. Ottimo segno: i tuoi messaggi non infastidiscono i clienti.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          <div className="lg:col-span-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-[#1F2937]">
                <th className="px-5 py-2.5">Campagna</th><th className="px-3 py-2.5 text-right">Inviati</th>
                <th className="px-3 py-2.5 text-right">STOP</th><th className="px-3 py-2.5 text-right">% STOP</th><th className="px-3 py-2.5">Motivo principale</th><th className="px-5 py-2.5 text-right">Ultimo</th>
              </tr></thead>
              <tbody data-testid="report-unsubscribes-table">
                {data.by_campaign.map((r) => (
                  <tr key={r.id} className="border-b border-[#1F2937]/60" data-testid={`report-unsub-row-${r.id}`}>
                    <td className="px-5 py-2.5 text-slate-200">{r.name}{r.alerted && <span title="Email di avviso soglia inviata all'admin" data-testid={`report-unsub-alerted-${r.id}`} className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-[#F59E0B]"><MailWarning className="h-3 w-3" /> avvisato</span>}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{r.sent}</td>
                    <td className="px-3 py-2.5 text-right text-[#EF4444] font-semibold">{r.stops}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`font-semibold ${r.rate >= 2 ? "text-[#EF4444]" : r.rate >= 0.5 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{r.rate == null ? "–" : `${r.rate}%`}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`report-unsub-top-reason-${r.id}`}>
                      {r.top_reason ? <button type="button" onClick={() => setFilter({ reason: r.top_reason.reason, campaign_id: r.id, campaign_name: r.name })} className="text-slate-200 hover:text-[#10B981] underline decoration-dotted text-left">{r.top_reason.reason} <span className="text-slate-500">({r.top_reason.count}/{r.top_reason.total})</span></button> : <span className="text-slate-600">–</span>}
                    </td>
                    <td className="px-5 py-2.5 text-right text-xs text-slate-500">{r.last_at ? new Date(r.last_at).toLocaleDateString("it-IT") : ""}</td>
                  </tr>
                ))}
                {!data.by_campaign.length && <tr><td colSpan={6} className="px-5 py-4 text-slate-500 text-sm">Nessuno STOP riconducibile a una campagna recente.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border-t lg:border-t-0 lg:border-l border-[#1F2937] p-5 space-y-4">
            {data.by_reason?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Motivi dichiarati</p>
                <div className="space-y-1.5" data-testid="report-unsubscribes-reasons">
                  {data.by_reason.map((r) => {
                    const tot = data.by_reason.reduce((a, x) => a + x.count, 0);
                    return (
                      <button type="button" key={r.reason} onClick={() => setFilter({ reason: r.reason })} data-testid={`report-unsub-reason-${r.reason}`}
                              className="w-full text-left rounded px-1 -mx-1 hover:bg-[#1F2937]/60 transition-colors" title="Vedi i clienti che hanno indicato questo motivo">
                        <div className="flex items-center justify-between text-sm"><span className="text-slate-200">{r.reason}</span><span className="text-slate-400">{r.count} · {Math.round(r.count * 100 / tot)}%</span></div>
                        <div className="h-1.5 rounded-full bg-[#1F2937] mt-1"><div className="h-1.5 rounded-full bg-[#EF4444]" style={{ width: `${Math.round(r.count * 100 / tot)}%` }} /></div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-600 mt-1">Clicca un motivo per vedere l'elenco dei clienti.</p>
              </div>
            )}
            <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Parole usate</p>
            <div className="space-y-1.5" data-testid="report-unsubscribes-words">
              {data.by_word.map((w) => (
                <div key={w.word} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-slate-300">“{w.word}”</span>
                  <span className="text-slate-400">{w.count}</span>
                </div>
              ))}
            </div>
            </div>
            <p className="text-[11px] text-slate-500">Oltre il 2% di STOP su una campagna indica un messaggio percepito come invadente: rivedi testo, frequenza o segmento.{!data.by_reason?.length && " Attiva l'automazione «Disiscrizione con motivo» (Consensi GDPR) per vedere qui i motivi."}</p>
          </div>
        </div>
      )}
      <UnsubContactsDialog filter={filter} range={range} onClose={() => setFilter(null)} />
    </div>
  );
}
